import { useState } from 'react';
import { contrast } from '@circles/tokens/color';
import { deriveAccent } from '@circles/tokens/accent';
import { themes } from '@circles/tokens';
import { circlesLogoUrl } from '../state/theme';
import { cx } from '../components/ui';
import { getState, incomingCall, setState, useStore, type Layout } from '../state/store';
import { applyPreset, presets } from './presets';

const LAYOUTS: { id: Layout; label: string; hint: string }[] = [
  { id: 'phone', label: 'Phone', hint: 'iPhone and Android' },
  { id: 'duoOuter', label: 'Duo · outer 5.4"', hint: 'Folded' },
  { id: 'duoInner', label: 'Duo · inner 7.6"', hint: 'Unfolded, two panes' },
  { id: 'duoSplit', label: 'Duo · Split View', hint: 'Next to another app' },
  { id: 'desktopMac', label: 'Desktop · macOS', hint: 'Two panes' },
  { id: 'desktopWin', label: 'Desktop · Windows', hint: 'Two panes' },
];

const ORGS: { id: string | null; label: string; swatch: string }[] = [
  { id: 'nordwind', label: themes.nordwind.name, swatch: themes.nordwind.accent.seed },
  { id: 'praxis', label: themes.praxis.name, swatch: themes.praxis.accent.seed },
  { id: null, label: 'No organisation', swatch: themes.circles.accent.seed },
];

export function ControlPanel() {
  const layout = useStore((s) => s.layout);
  const mode = useStore((s) => s.mode);
  const split = useStore((s) => s.split);
  const orgId = useStore((s) => s.orgId);
  const stage = useStore((s) => s.auth.stage);
  const custom = useStore((s) => s.customAccent);
  const [open, setOpen] = useState(false);
  const [picker, setPicker] = useState(custom ?? '#7A3DB8');
  const derived = deriveAccent(picker);

  const chooseOrg = (id: string | null) => {
    const s = getState();
    setState({
      orgId: id,
      auth: {
        stage: s.auth.stage === 'in' ? 'in' : s.auth.stage,
        email:
          id === null
            ? 'paul.schreiber@example.com'
            : id === 'praxis'
              ? 'm.falk@praxis-am-park.de'
              : 'anna.berg@nordwind-logistik.de',
      },
      stacks: { chats: [], feed: [], calls: [], you: [] },
      tab: id === null && s.tab === 'feed' ? 'chats' : s.tab,
      call: null,
    });
  };

  const groups = [...new Set(presets.map((p) => p.group))];

  return (
    <aside className={cx('panel', open && 'is-open')}>
      <div className="panel-head">
        <img src={circlesLogoUrl} alt="" width={30} height={30} />
        <div>
          <strong>Circles prototype</strong>
          <span>Task 0.7 · mock data, nothing is sent</span>
        </div>
        <button
          type="button"
          className="panel-toggle"
          onClick={() => setOpen(!open)}
          aria-expanded={open}
        >
          {open ? 'Close' : 'Controls'}
        </button>
      </div>
      <div className="panel-body">
        <section className="panel-section">
          <h2>Layout</h2>
          <div className="panel-options">
            {LAYOUTS.map((l) => (
              <button
                type="button"
                key={l.id}
                className={cx('panel-option', layout === l.id && 'is-selected')}
                onClick={() => setState({ layout: l.id })}
              >
                <LayoutGlyph id={l.id} />
                <span>
                  {l.label}
                  <small>{l.hint}</small>
                </span>
              </button>
            ))}
          </div>
          {layout === 'duoInner' && (
            <button
              type="button"
              className="panel-link"
              onClick={() => setState((s) => ({ landscape: !s.landscape }))}
            >
              Rotate screen
            </button>
          )}
          {layout === 'duoSplit' && (
            <div className="panel-seg">
              {(['third', 'half', 'twoThirds'] as const).map((v) => (
                <button
                  type="button"
                  key={v}
                  className={cx(split === v && 'is-selected')}
                  onClick={() => setState({ split: v })}
                >
                  {v === 'third' ? '⅓' : v === 'half' ? '½' : '⅔'}
                </button>
              ))}
            </div>
          )}
        </section>

        <section className="panel-section">
          <h2>Appearance</h2>
          <div className="panel-seg">
            {(['light', 'dark'] as const).map((m) => (
              <button
                type="button"
                key={m}
                className={cx(mode === m && 'is-selected')}
                onClick={() => setState({ mode: m })}
              >
                {m === 'light' ? 'Light' : 'Dark'}
              </button>
            ))}
          </div>
        </section>

        <section className="panel-section">
          <h2>Organisation design</h2>
          <p className="panel-note">
            Downloaded after sign-in and applied at runtime. No app update needed.
          </p>
          <div className="panel-options">
            {ORGS.map((o) => (
              <button
                type="button"
                key={String(o.id)}
                className={cx('panel-option', stage === 'in' && orgId === o.id && 'is-selected')}
                onClick={() => chooseOrg(o.id)}
              >
                <span className="swatch" style={{ background: o.swatch }} />
                <span>
                  {o.label}
                  <small>
                    {o.id === null
                      ? 'Neutral Circles · Chats and Calls only'
                      : `Accent ${o.swatch}`}
                  </small>
                </span>
              </button>
            ))}
            <div className={cx('panel-option custom', orgId === 'custom' && 'is-selected')}>
              <input
                type="color"
                value={picker}
                aria-label="Custom accent colour"
                onChange={(e) => {
                  setPicker(e.target.value);
                  setState({ customAccent: e.target.value, orgId: 'custom' });
                }}
              />
              <span>
                Try any accent
                <small>
                  Buttons {derived.light.action} ·{' '}
                  {contrast(derived.light.action, '#FFFFFF').toFixed(1)}:1 light,{' '}
                  {contrast(derived.dark.action, '#101216').toFixed(1)}:1 dark
                </small>
              </span>
            </div>
          </div>
        </section>

        <section className="panel-section">
          <h2>Jump to</h2>
          {groups.map((g) => (
            <div key={g} className="panel-jump">
              <h3>{g}</h3>
              <div className="panel-chips">
                {presets
                  .filter((p) => p.group === g)
                  .map((p) => (
                    <button type="button" key={p.id} onClick={() => applyPreset(p.id)}>
                      {p.label}
                    </button>
                  ))}
              </div>
            </div>
          ))}
          <button
            type="button"
            className="panel-link"
            onClick={incomingCall}
            disabled={stage !== 'in'}
          >
            Ring me now (incoming call)
          </button>
        </section>
        <p className="panel-foot">
          Built on the design tokens in <code>web/tokens</code>. Spec sections 2, 4 and 5.
        </p>
      </div>
    </aside>
  );
}

function LayoutGlyph({ id }: { id: Layout }) {
  const common = { fill: 'none', stroke: 'currentColor', strokeWidth: 1.5 } as const;
  return (
    <svg className="layout-glyph" viewBox="0 0 28 22" width="28" height="22" aria-hidden>
      {id === 'phone' && <rect x="9" y="1" width="10" height="20" rx="2.5" {...common} />}
      {id === 'duoOuter' && <rect x="9.5" y="2" width="9" height="18" rx="2.5" {...common} />}
      {id === 'duoInner' && (
        <>
          <rect x="5" y="2" width="18" height="18" rx="2.5" {...common} />
          <path d="M14 2v18" {...common} strokeDasharray="2 2" />
        </>
      )}
      {id === 'duoSplit' && (
        <>
          <rect x="2" y="3" width="24" height="16" rx="2.5" {...common} />
          <path d="M13 3v16" {...common} />
        </>
      )}
      {(id === 'desktopMac' || id === 'desktopWin') && (
        <>
          <rect x="2" y="2" width="24" height="15" rx="1.5" {...common} />
          <path d="M10 21h8M14 17v4" {...common} />
        </>
      )}
    </svg>
  );
}
